import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Flame, Trophy, Loader2, Target } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WORD_LEN = 5;
const MAX_ATTEMPTS = 6;

const WordlePage = () => {
  const [state, setState] = useState(null);
  const [current, setCurrent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [stateRes, lbRes] = await Promise.all([
        axios.get(`${API_URL}/api/wordle/today`, { withCredentials: true }),
        axios.get(`${API_URL}/api/wordle/leaderboard`, { withCredentials: true }),
      ]);
      setState(stateRes.data);
      setLeaderboard(lbRes.data?.winners || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => {
      if (state?.status !== 'playing') return;
      if (e.key === 'Enter') { submitGuess(); }
      else if (e.key === 'Backspace') { setCurrent(c => c.slice(0, -1)); }
      else if (/^[a-zA-Z]$/.test(e.key) && current.length < WORD_LEN) {
        setCurrent(c => (c + e.key).toUpperCase().slice(0, WORD_LEN));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, state?.status]);

  const submitGuess = async () => {
    if (current.length !== WORD_LEN) { toast.error('Enter a 5-letter word'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/wordle/submit`, { guess: current }, { withCredentials: true });
      setState(res.data);
      setCurrent('');
      if (res.data?.status === 'won') {
        toast.success(`🎉 Solved in ${res.data.guesses.length}! +${res.data.awarded || 0} Witties (streak: ${res.data.streak || 1})`);
        fetchAll();
      } else if (res.data?.status === 'lost') {
        toast.error(`Out of attempts. Word was: ${res.data.answer_revealed}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  const cellClass = (status) => {
    if (status === 'correct') return 'bg-emerald-500 text-white border-emerald-500';
    if (status === 'present') return 'bg-amber-500 text-white border-amber-500';
    if (status === 'absent') return 'bg-slate-400 text-white border-slate-400 dark:bg-slate-600 dark:border-slate-600';
    return 'bg-background border-border';
  };

  // ---- Per-letter keyboard status (correct > present > absent > untouched) ----
  const keyStatuses = React.useMemo(() => {
    const map = {};
    if (!state) return map;
    const guesses = state.guesses || [];
    const feedback = state.feedback || [];
    const rank = { correct: 3, present: 2, absent: 1 };
    guesses.forEach((g, gi) => {
      const f = feedback[gi] || [];
      for (let i = 0; i < (g || '').length; i++) {
        const ch = g[i].toUpperCase();
        const st = f[i];
        if (!st) continue;
        if (!map[ch] || rank[st] > rank[map[ch]]) map[ch] = st;
      }
    });
    return map;
  }, [state]);

  const keyClass = (k) => {
    const st = keyStatuses[k];
    if (st === 'correct') return 'bg-emerald-500 text-white hover:bg-emerald-600 border-emerald-500';
    if (st === 'present') return 'bg-amber-500 text-white hover:bg-amber-600 border-amber-500';
    if (st === 'absent') return 'bg-slate-400 text-white hover:bg-slate-500 dark:bg-slate-700 dark:border-slate-700 border-slate-400 opacity-70';
    return 'bg-muted hover:bg-muted/70 text-foreground border-transparent';
  };

  const handleKeyTap = (key) => {
    if (state?.status !== 'playing') return;
    if (key === 'ENTER') { submitGuess(); return; }
    if (key === 'BACK') { setCurrent(c => c.slice(0, -1)); return; }
    if (current.length < WORD_LEN) {
      setCurrent(c => (c + key).slice(0, WORD_LEN));
    }
  };

  const KB_ROWS = [
    ['Q','W','E','R','T','Y','U','I','O','P'],
    ['A','S','D','F','G','H','J','K','L'],
    ['ENTER','Z','X','C','V','B','N','M','BACK'],
  ];

  const renderRows = () => {
    if (!state) return null;
    const guesses = state.guesses || [];
    const feedback = state.feedback || [];
    const rows = [];
    for (let r = 0; r < MAX_ATTEMPTS; r++) {
      const g = guesses[r] || (r === guesses.length ? current.padEnd(WORD_LEN, ' ') : '     ');
      const f = feedback[r] || [];
      rows.push(
        <div key={r} className="flex gap-2 justify-center">
          {Array.from({ length: WORD_LEN }).map((_, i) => (
            <div
              key={i}
              data-testid={`wordle-cell-${r}-${i}`}
              className={`w-14 h-14 sm:w-16 sm:h-16 border-2 rounded flex items-center justify-center text-2xl font-bold uppercase transition-all ${cellClass(f[i])} ${!f[i] && g[i] && g[i] !== ' ' ? 'border-primary' : ''}`}
            >
              {(g[i] && g[i] !== ' ') ? g[i] : ''}
            </div>
          ))}
        </div>
      );
    }
    return rows;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wordle-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Target className="w-7 h-7 text-amber-500" /> Daily Wordle</h1>
          <p className="text-muted-foreground text-sm">Guess the 5-letter word. Earn Witties for solving — bigger streak, bigger reward.</p>
        </div>
        <div className="flex items-center gap-2">
          {state?.streak > 0 && (
            <Badge className="gap-1 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
              <Flame className="w-3.5 h-3.5" /> {state.streak}-day streak
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">Attempt {Math.min((state?.guesses?.length || 0) + (state?.status === 'playing' ? 1 : 0), MAX_ATTEMPTS)}/{MAX_ATTEMPTS}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="space-y-2">{renderRows()}</div>

            {state?.status === 'playing' && (
              <div className="mt-6 space-y-2 select-none" data-testid="wordle-keyboard">
                {KB_ROWS.map((row, ri) => (
                  <div key={ri} className="flex gap-1.5 sm:gap-2 justify-center">
                    {row.map((k) => {
                      const isWide = k === 'ENTER' || k === 'BACK';
                      const label = k === 'BACK' ? '⌫' : k === 'ENTER' ? 'Enter' : k;
                      const disabled = submitting || (k === 'ENTER' && current.length !== WORD_LEN);
                      return (
                        <button
                          key={k}
                          onClick={() => handleKeyTap(k)}
                          disabled={disabled}
                          data-testid={`wordle-key-${k}`}
                          className={`${keyClass(k)} ${isWide ? 'px-3 sm:px-4 text-xs sm:text-sm' : 'w-8 sm:w-10 text-base sm:text-lg'} h-12 sm:h-14 rounded-md font-semibold border-2 transition-all duration-150 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {state?.status === 'won' && (
              <div className="mt-6 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/40 text-center">
                <Trophy className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                <p className="font-bold text-emerald-700 dark:text-emerald-400">Solved in {state.guesses?.length}!</p>
                {state.witties_awarded > 0 && <p className="text-sm text-muted-foreground">+{state.witties_awarded} Witties earned</p>}
                {state.streak >= 1 && <p className="text-sm">Current streak: <span className="font-semibold">{state.streak} days</span></p>}
                <p className="text-xs text-muted-foreground mt-2">Come back tomorrow for the next puzzle!</p>
              </div>
            )}
            {state?.status === 'lost' && (
              <div className="mt-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-center">
                <p className="font-bold text-red-700 dark:text-red-400">Out of attempts</p>
                {state.answer_revealed && <p className="text-sm text-muted-foreground mt-1">Answer was: <span className="font-mono font-bold">{state.answer_revealed}</span></p>}
              </div>
            )}

            <p className="text-center text-xs text-muted-foreground mt-4">
              Use your keyboard or tap letters · <span className="text-emerald-500">●</span> right spot · <span className="text-amber-500">●</span> wrong spot · <span className="text-slate-500">●</span> not in word
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Today's Leaderboard</CardTitle>
            <CardDescription>Top solvers (fewest attempts wins)</CardDescription>
          </CardHeader>
          <CardContent>
            {leaderboard.length > 0 ? (
              <div className="space-y-2">
                {leaderboard.slice(0, 10).map((w, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded hover:bg-muted/50">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30' : i === 1 ? 'bg-slate-200 text-slate-700 dark:bg-slate-700' : i === 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30' : 'bg-muted'}`}>{i + 1}</span>
                    <span className="text-sm flex-1 truncate">{w.name}</span>
                    <Badge variant="outline" className="text-xs">{w.attempts}/6</Badge>
                    {w.streak > 1 && <Badge className="text-xs gap-1 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"><Flame className="w-3 h-3" />{w.streak}</Badge>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-6 text-sm">No one's solved it yet today. Be the first!</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WordlePage;
